import React from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
  Grid,
  Chip,
  Divider,
  Paper,
  Alert
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import ChipListEditor from './ChipListEditor';
import {
  cleanListValors,
  updateAdapterListFilter
} from '../../functions/syncConfigSanitizer';

/**
 * AdaptersSection
 * Gestiona la llista de connectors/adaptadors bancaris del perfil seleccionat,
 * incloent opcions generals, filtres d'inclusió, filtres d'exclusió i accés al detall.
 */
const AdaptersSection = ({
  adapters = {},
  initialAdapters = {},
  onChange,
  disabled = false
}) => {
  const adapterKeys = Object.keys(adapters || {});

  const handleAdapterChange = (adapterKey, field, value) => {
    const currentAdapter = adapters[adapterKey] || {};
    const updatedAdapter = {
      ...currentAdapter,
      [field]: value
    };
    onChange({
      ...adapters,
      [adapterKey]: updatedAdapter
    });
  };

  const handleFilterChange = (adapterKey, targetSection, targetKey, newItems) => {
    const currentAdapter = adapters[adapterKey] || {};
    const initialAdapter = initialAdapters?.[adapterKey] || {};
    const updatedAdapter = updateAdapterListFilter(
      currentAdapter,
      initialAdapter,
      targetSection,
      targetKey,
      newItems
    );

    onChange({
      ...adapters,
      [adapterKey]: updatedAdapter
    });
  };

  if (adapterKeys.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No hi ha cap adaptador bancari configurat per a aquest perfil.
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography
        variant="h6"
        sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <AccountBalanceIcon color="primary" /> Adaptadors Bancaris Configurats
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configura l'activació, automatització, sincronització de comptes/targetes i regles d'inclusió i exclusió per a cada entitat.
      </Typography>

      {adapterKeys.map((key, index) => {
        const adapter = adapters[key] || {};
        const isEnabled = adapter.enabled ?? initialAdapters?.[key]?.enabled ?? false;
        const isAuto = adapter.auto ?? initialAdapters?.[key]?.auto ?? false;
        const comptesEnabled =
          adapter['comptes-enabled'] ??
          adapter.comptesEnabled ??
          initialAdapters?.[key]?.['comptes-enabled'] ??
          initialAdapters?.[key]?.comptesEnabled ??
          false;
        const targetesEnabled =
          adapter['targetes-enabled'] ??
          adapter.targetesEnabled ??
          initialAdapters?.[key]?.['targetes-enabled'] ??
          initialAdapters?.[key]?.targetesEnabled ??
          false;

        // 1. Filtres d'Inclusió (filtres.incloure)
        const rawIncloureComptes =
          adapter.filtres?.incloure?.compte?.['list-valors'] ??
          adapter.filtres?.incloure?.compte?.listValors ??
          [];
        const incloureComptes = cleanListValors(rawIncloureComptes);

        const rawIncloureTargetes =
          adapter.filtres?.incloure?.targeta?.['list-valors'] ??
          adapter.filtres?.incloure?.targeta?.listValors ??
          [];
        const incloureTargetes = cleanListValors(rawIncloureTargetes);

        // 2. Filtres d'Exclusió (filtres.excloure)
        const rawExcloureMoviments =
          adapter.filtres?.excloure?.moviment?.['list-valors'] ??
          adapter.filtres?.excloure?.moviment?.listValors ??
          [];
        const excloureMoviments = cleanListValors(rawExcloureMoviments);

        const rawExcloureComptes =
          adapter.filtres?.excloure?.compte?.['list-valors'] ??
          adapter.filtres?.excloure?.compte?.listValors ??
          [];
        const excloureComptes = cleanListValors(rawExcloureComptes);

        // 3. Accés al Detall (access-detall)
        const rawAccessDetall =
          adapter['access-detall']?.moviment?.['list-valors'] ??
          adapter.accessDetall?.moviment?.['list-valors'] ??
          adapter.accessDetall?.moviment?.listValors ??
          [];
        const accessDetallMoviments = cleanListValors(rawAccessDetall);

        return (
          <Accordion
            key={key}
            defaultExpanded={index === 0}
            sx={{
              mb: 2,
              borderRadius: '8px !important',
              border: '1px solid #e0e0e0',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              '&:before': { display: 'none' }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: isEnabled ? '#f8fafc' : '#f5f5f5',
                borderRadius: '8px',
                px: 2
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, width: '100%', mr: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, minWidth: 150 }}>
                  {adapter.nom || key.toUpperCase()}
                </Typography>
                <Chip
                  size="small"
                  label={isEnabled ? 'Actiu' : 'Desactivat'}
                  color={isEnabled ? 'success' : 'default'}
                  variant={isEnabled ? 'filled' : 'outlined'}
                />
                {isAuto && (
                  <Chip size="small" label="Auto programat" color="info" variant="outlined" />
                )}
                {comptesEnabled && (
                  <Chip size="small" label="Comptes" color="primary" variant="outlined" />
                )}
                {targetesEnabled && (
                  <Chip size="small" label="Targetes" color="secondary" variant="outlined" />
                )}
                {incloureComptes.length > 0 && (
                  <Chip
                    size="small"
                    label={`Filtre inclusió: ${incloureComptes.length} cpt`}
                    color="info"
                  />
                )}
                {excloureMoviments.length > 0 && (
                  <Chip
                    size="small"
                    label={`Exclosos: ${excloureMoviments.length} mov`}
                    color="warning"
                    variant="outlined"
                  />
                )}
              </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ p: 3 }}>
              {/* Secció 1: Opcions Generals del Conector */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main', mb: 1.5 }}>
                Opcions Generals del Conector
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isEnabled}
                          onChange={(e) => handleAdapterChange(key, 'enabled', e.target.checked)}
                          disabled={disabled}
                          color="success"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Habilitar Conector
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Activa aquest adaptador
                          </Typography>
                        </Box>
                      }
                    />
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isAuto}
                          onChange={(e) => handleAdapterChange(key, 'auto', e.target.checked)}
                          disabled={disabled || !isEnabled}
                          color="info"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Execució Automàtica
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Sincronització automàtica
                          </Typography>
                        </Box>
                      }
                    />
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={comptesEnabled}
                          onChange={(e) => handleAdapterChange(key, 'comptes-enabled', e.target.checked)}
                          disabled={disabled || !isEnabled}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Comptes Corrents
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Sincronitza comptes
                          </Typography>
                        </Box>
                      }
                    />
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={targetesEnabled}
                          onChange={(e) => handleAdapterChange(key, 'targetes-enabled', e.target.checked)}
                          disabled={disabled || !isEnabled}
                          color="secondary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Targetes
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Sincronitza targetes
                          </Typography>
                        </Box>
                      }
                    />
                  </Paper>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Secció 2: Filtres d'Inclusió (Comptes / Targetes) */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CheckCircleOutlineIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Filtres d'Inclusió (filtres.incloure)
                  </Typography>
                </Box>
                <Alert severity="info" sx={{ mb: 2, fontSize: '0.85rem', py: 0.5 }}>
                  Per defecte se sincronitzen <strong>tots els comptes i targetes</strong> de l'entitat. Si afegeixes valors aquí, el connector <strong>NOMÉS</strong> sincronitzarà els comptes o targetes especificats. Si deixes les llistes buides, el filtre queda inactiu i se sincronitzen tots sense descartar-ne cap.
                </Alert>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <ChipListEditor
                      label="Comptes a Incloure (filtres.incloure.compte)"
                      helperText="Especifica els IBANs o identificadors de comptes a sincronitzar. Buit = tots els comptes."
                      items={incloureComptes}
                      onChange={(newList) => handleFilterChange(key, 'incloure', 'compte', newList)}
                      placeholder="Ex: ES12345678901234567890..."
                      color="primary"
                      disabled={disabled || !isEnabled}
                      isMonospace={true}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ChipListEditor
                      label="Targetes a Incloure (filtres.incloure.targeta)"
                      helperText="Especifica les màscares o números de targeta a sincronitzar. Buit = totes les targetes."
                      items={incloureTargetes}
                      onChange={(newList) => handleFilterChange(key, 'incloure', 'targeta', newList)}
                      placeholder="Ex: 4548... o número de targeta..."
                      color="primary"
                      disabled={disabled || !isEnabled}
                      isMonospace={true}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Secció 3: Filtres d'Exclusió */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <FilterAltIcon color="warning" fontSize="small" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Filtres d'Exclusió (filtres.excloure)
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={8}>
                    <ChipListEditor
                      label="Moviments Exclosos (filtres.excloure.moviment)"
                      helperText="Els moviments que coincideixin amb aquests patrons o literals s'ometran durant la sincronització (ex. CAJ.LA CAIXA OF., MYCARD OR EMPLEAT)."
                      items={excloureMoviments}
                      onChange={(newList) => handleFilterChange(key, 'excloure', 'moviment', newList)}
                      placeholder="Escriu un literal de moviment a excloure i prem Enter..."
                      color="warning"
                      disabled={disabled || !isEnabled}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <ChipListEditor
                      label="Comptes Exclosos (filtres.excloure.compte)"
                      helperText="Opcional. Identificadors de comptes a excloure de la sincronització."
                      items={excloureComptes}
                      onChange={(newList) => handleFilterChange(key, 'excloure', 'compte', newList)}
                      placeholder="Ex: ES9876..."
                      color="warning"
                      disabled={disabled || !isEnabled}
                      isMonospace={true}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Secció 4: Accés al Detall de Moviments */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <SearchIcon color="action" fontSize="small" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Accés al Detall de Moviments (access-detall)
                  </Typography>
                </Box>
                <ChipListEditor
                  label="Moviments amb Desglossament / Detall (access-detall.moviment)"
                  helperText="Moviments que requereixen consultar el detall o desglossament en la sincronització (ex. BIZUM ENVIAT, BIZUM REBUT, TRANSF AL SEU FAVOR)."
                  items={accessDetallMoviments}
                  onChange={(newList) => handleFilterChange(key, 'access-detall', 'moviment', newList)}
                  placeholder="Escriu un moviment que requereixi detall i prem Enter..."
                  color="info"
                  disabled={disabled || !isEnabled}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default AdaptersSection;
